package co.maytes.standupbot.dashboard.features.settings.sections

import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.features.settings.phase
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.ConfirmModal
import co.maytes.standupbot.dashboard.components.feedback.ConfirmTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Select
import co.maytes.standupbot.dashboard.components.widgets.SelectOption
import co.maytes.standupbot.dashboard.features.integrations.LoadState
import co.maytes.standupbot.dashboard.features.settings.Plain
import co.maytes.standupbot.dashboard.features.settings.RetentionCopy
import co.maytes.standupbot.dashboard.features.settings.RetentionWindow
import co.maytes.standupbot.dashboard.features.settings.RetentionWindowStrip
import co.maytes.standupbot.dashboard.features.settings.SettingsBars
import co.maytes.standupbot.dashboard.features.settings.SettingsError
import co.maytes.standupbot.dashboard.features.settings.SettingsField
import co.maytes.standupbot.dashboard.features.settings.SettingsGroup
import co.maytes.standupbot.dashboard.features.settings.SettingsPage
import co.maytes.standupbot.dashboard.features.settings.SettingsPanel
import co.maytes.standupbot.dashboard.features.settings.SettingsPreview
import co.maytes.standupbot.dashboard.features.settings.SettingsRowItem
import co.maytes.standupbot.dashboard.features.settings.SettingsSaveBar
import co.maytes.standupbot.dashboard.features.settings.SettingsSectionRow
import co.maytes.standupbot.dashboard.features.settings.SettingsSubtitle
import co.maytes.standupbot.dashboard.features.settings.Strong
import co.maytes.standupbot.dashboard.features.settings.WorkspaceExportCopy
import co.maytes.standupbot.dashboard.features.settings.downloadBytes
import co.maytes.standupbot.dashboard.features.settings.rememberSettingsStore
import co.maytes.standupbot.dashboard.features.settings.rememberWorkspacePatcher
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.shared.dto.auth.RoleDto
import co.maytes.standupbot.shared.dto.workspace.UpdateWorkspaceRequestDto
import co.maytes.standupbot.shared.dto.workspace.WorkspaceDto
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn

@Composable
fun DataSection() {
    val auth = rememberAuthStore()
    val store = rememberSettingsStore()
    val scope = rememberCoroutineScope()
    val workspaceId = auth.currentUser?.workspace?.id

    LaunchedEffect(workspaceId) {
        if (!workspaceId.isNullOrBlank()) store.refreshWorkspace(workspaceId)
    }

    val ws = store.workspace
    SettingsPage(
        group = SettingsGroup.SecurityAndData,
        title = "Data and privacy",
        subtitle = ws?.let {
            {
                SettingsSubtitle {
                    Plain("Raw activity is kept ")
                    Strong(RetentionCopy.rawActivityLabel(it.rawActivityRetentionDays))
                    Plain("; summaries and standup text are kept.")
                }
            }
        },
    ) {
        StateSwap(state = store.workspaceState.phase()) {
            when (store.workspaceState) {
                LoadState.Idle, LoadState.Loading -> {
                    SettingsSectionRow(anchor = "retention", title = "Retention", description = RetentionCopy.RAW_ACTIVITY_HINT) {
                        SettingsPanel { SettingsBars(rows = 3) }
                    }
                }
                is LoadState.Error -> SettingsSectionRow(
                    anchor = "retention",
                    title = "Retention",
                    description = RetentionCopy.RAW_ACTIVITY_HINT,
                ) {
                    SettingsPanel {
                        SettingsError(what = "the workspace settings") {
                            scope.launch { workspaceId?.let { store.refreshWorkspace(it) } }
                        }
                    }
                }
                LoadState.Ready -> if (ws != null) {
                    RetentionPanel(ws)
                    ExportPanel()
                    DangerPanel(ws)
                }
            }
        }
    }
}

@Composable
private fun RetentionPanel(ws: WorkspaceDto) {
    val patcher = rememberWorkspacePatcher()
    var draft by remember(ws.id, ws.rawActivityRetentionDays) { mutableStateOf(ws.rawActivityRetentionDays) }
    val dirty = draft != ws.rawActivityRetentionDays
    val today = Clock.System.todayIn(TimeZone.UTC)

    SettingsSectionRow(
        anchor = "retention",
        title = "Retention",
        description = RetentionCopy.RAW_ACTIVITY_HINT,
    ) {
        SettingsPanel {
            SettingsField(label = "Raw activity kept for") {
                Select(
                    value = draft,
                    options = buildList {
                        RetentionCopy.rawActivityChoices.forEach { add(SelectOption(it, RetentionCopy.rawActivityLabel(it))) }
                        if (draft !in RetentionCopy.rawActivityChoices) {
                            add(SelectOption(draft, RetentionCopy.rawActivityLabel(draft)))
                        }
                    },
                    onValueChange = { draft = it },
                    modifier = Modifier.fillMaxWidth().styleModifier { property("max-width", MAX_FIELD_WIDTH) },
                )
            }
            RetentionWindowStrip(keptDays = draft)
            SettingsPreview(label = "What this does tonight") { Plain(RetentionWindow.describe(today, draft)) }
            SettingsSaveBar(
                open = dirty,
                saving = patcher.saving,
                hint = patcher.error ?: "Unsaved changes",
                onSave = {
                    patcher.save(UpdateWorkspaceRequestDto(rawActivityRetentionDays = draft), "Retention saved")
                },
                onReset = { draft = ws.rawActivityRetentionDays },
            )
        }
    }
}

@Composable
private fun ExportPanel() {
    val auth = rememberAuthStore()
    val store = rememberSettingsStore()
    val ui = rememberUiStore()
    val scope = rememberCoroutineScope()
    val workspaceId = auth.currentUser?.workspace?.id
    var exporting by remember { mutableStateOf(false) }
    val isOwner = auth.currentUser?.role == RoleDto.OWNER

    SettingsSectionRow(
        anchor = "export",
        title = "Export",
        description = WorkspaceExportCopy.DESCRIPTION,
    ) {
        SettingsPanel {
            SettingsRowItem(
                title = WorkspaceExportCopy.TITLE,
                first = true,
                description = if (isOwner) {
                    "Members, standups, runs, prompt archive, audit log and settings as JSON."
                } else {
                    "Only the workspace owner can export data."
                },
                trailing = {
                    Button(
                        label = if (exporting) WorkspaceExportCopy.BUTTON_BUSY else WorkspaceExportCopy.BUTTON,
                        variant = ButtonVariant.Secondary,
                        size = ButtonSize.Sm,
                        enabled = isOwner && !exporting && !workspaceId.isNullOrBlank(),
                        loading = exporting,
                        onClick = {
                            val wid = workspaceId ?: return@Button
                            scope.launch {
                                exporting = true
                                when (val result = store.exportWorkspaceData(wid)) {
                                    is Result.Ok -> {
                                        downloadBytes(
                                            result.value,
                                            WorkspaceExportCopy.fileName(Clock.System.todayIn(TimeZone.UTC)),
                                            WorkspaceExportCopy.ZIP_MIME_TYPE,
                                        )
                                        ui.showToast(WorkspaceExportCopy.DONE, ToastKind.Success)
                                    }
                                    is Result.Err ->
                                        ui.showToast(WorkspaceExportCopy.failureMessage(result.error), ToastKind.Error)
                                }
                                exporting = false
                            }
                        },
                    )
                },
            )
        }
    }
}

@Composable
private fun DangerPanel(ws: WorkspaceDto) {
    val auth = rememberAuthStore()
    val store = rememberSettingsStore()
    val ui = rememberUiStore()
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val workspaceId = auth.currentUser?.workspace?.id
    var showUninstall by remember { mutableStateOf(false) }
    var uninstalling by remember { mutableStateOf(false) }
    var showDelete by remember { mutableStateOf(false) }
    var deleting by remember { mutableStateOf(false) }

    SettingsSectionRow(
        anchor = "danger-zone",
        title = "Danger zone",
        description = "Both ask you to confirm first.",
    ) {
        SettingsPanel {
            SettingsRowItem(
                title = "Uninstall",
                first = true,
                description = RetentionCopy.UNINSTALL_DESCRIPTION,
                trailing = {
                    Button(
                        label = "Uninstall",
                        variant = ButtonVariant.DangerOutline,
                        size = ButtonSize.Sm,
                        onClick = { showUninstall = true },
                    )
                },
            )
            SettingsRowItem(
                title = "Delete workspace",
                description = RetentionCopy.DELETE_DESCRIPTION,
                trailing = {
                    Button(
                        label = "Delete workspace",
                        variant = ButtonVariant.DangerOutline,
                        size = ButtonSize.Sm,
                        onClick = { showDelete = true },
                    )
                },
            )
        }
    }

    if (showUninstall) {
        ConfirmModal(
            title = "Uninstall this workspace?",
            body = "Everyone will be signed out. Standup history and integration tokens are kept; " +
                "reinstall StandupBot from Slack and nothing is lost.",
            confirmLabel = if (uninstalling) "Uninstalling…" else "Uninstall",
            cancelLabel = "Keep workspace",
            tone = ConfirmTone.Danger,
            onConfirm = {
                val wid = workspaceId ?: return@ConfirmModal
                if (uninstalling) return@ConfirmModal
                scope.launch {
                    uninstalling = true
                    when (val result = store.uninstallWorkspace(wid)) {
                        is Result.Ok -> {
                            showUninstall = false
                            auth.signOut()
                            ui.showToast("Workspace uninstalled", ToastKind.Success)
                            ctx.router.navigateTo("/")
                        }
                        is Result.Err -> {
                            ui.showToast("Uninstall failed: ${result.error.displayMessage}", ToastKind.Error)
                            uninstalling = false
                            showUninstall = false
                        }
                    }
                }
            },
            onDismiss = { if (!uninstalling) showUninstall = false },
        )
    }

    if (showDelete) {
        val expectedName = ws.slackTeamName
        ConfirmModal(
            title = "Delete this workspace?",
            body = RetentionCopy.deletionConfirmBody(),
            confirmLabel = if (deleting) "Scheduling…" else "Schedule deletion",
            cancelLabel = "Cancel",
            tone = ConfirmTone.Danger,
            confirmEnabled = !deleting,
            confirmMatch = expectedName,
            onConfirm = {
                val wid = workspaceId ?: return@ConfirmModal
                if (deleting) return@ConfirmModal
                scope.launch {
                    deleting = true
                    when (val result = store.requestWorkspaceDeletion(wid, expectedName)) {
                        is Result.Ok -> {
                            showDelete = false
                            auth.signOut()
                            ui.showToast(RetentionCopy.deletionScheduledMessage(result.value.purgeAfter), ToastKind.Success)
                            ctx.router.navigateTo("/")
                        }
                        is Result.Err -> {
                            ui.showToast("Couldn't schedule deletion: ${result.error.displayMessage}", ToastKind.Error)
                            deleting = false
                            showDelete = false
                        }
                    }
                }
            },
            onDismiss = { if (!deleting) showDelete = false },
        )
    }
}

private const val MAX_FIELD_WIDTH = "26rem"
